import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Upload,
  FileText,
  X,
  Zap,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Briefcase,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { LanguageSwitch } from '../../i18n/LanguageSwitch'
import { useLocale } from '../../i18n/LocaleProvider'
import { anonApi, uploadFileToS3 } from '../../lib/api'
import { clearAnonSession, getAnonSession, setAnonSession } from '../../lib/auth'
import { isValidCVFile, formatFileSize } from '../../lib/utils'
import toast from 'react-hot-toast'

export function AnonUpload() {
  const navigate = useNavigate()
  const { locale, t } = useLocale()
  const [step, setStep] = useState('setup') // setup | uploading | done
  const [jobTitle, setJobTitle] = useState('')
  const [jobDescription, setJobDescription] = useState('')
  const [files, setFiles] = useState([])
  const [sessionId, setSessionId] = useState(null)
  const [jobId, setJobId] = useState(null)
  const [uploadResults, setUploadResults] = useState([])
  const [uploading, setUploading] = useState(false)
  const [currentFile, setCurrentFile] = useState(null)
  const [progress, setProgress] = useState(0)
  const [existingSession, setExistingSession] = useState(null)

  const MAX_FILES = 5
  const MAX_PARALLEL_UPLOADS = 2

  useEffect(() => {
    const stored = getAnonSession()
    if (stored?.sessionId) {
      setExistingSession(stored)
    }
  }, [])

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        toast.error(t('anonUpload.toasts.invalidFiles'))
      }

      const validFiles = acceptedFiles.filter(isValidCVFile)
      const combined = [...files, ...validFiles].slice(0, MAX_FILES)
      setFiles(combined)

      if (combined.length >= MAX_FILES) {
        toast.success(t('anonUpload.toasts.maxReady', { count: MAX_FILES }))
      }
    },
    [files, t]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/msword': ['.doc'],
    },
    maxFiles: MAX_FILES,
    disabled: files.length >= MAX_FILES,
  })

  function removeFile(index) {
    setFiles(files.filter((_, i) => i !== index))
  }

  async function processFile(sessionValue, file) {
    const urlData = await anonApi.getUploadUrl(
      sessionValue,
      file.name,
      file.type || 'application/pdf'
    )

    await uploadFileToS3(urlData.uploadUrl, file)

    const parseResult = await anonApi.parseCV(
      sessionValue,
      urlData,
      file.name,
      locale
    )

    return {
      name: file.name,
      status: 'success',
      candidateId: parseResult.candidateId,
      candidateName: parseResult.name,
      matchScore: parseResult.matchScore,
    }
  }

  async function handleUpload() {
    if (!files.length) {
      toast.error(t('anonUpload.toasts.addCv'))
      return
    }

    setUploading(true)
    setStep('uploading')
    setProgress(0)
    setUploadResults([])

    try {
      // Create anonymous session
      const sessionData = await anonApi.createSession(jobTitle, jobDescription)
      const sid = sessionData.sessionId
      const jid = sessionData.jobId
      const session = {
        sessionId: sid,
        accessToken: sessionData.accessToken,
        claimToken: sessionData.claimToken,
        jobId: jid,
        jobTitle,
        jobDescription,
        maxCVs: sessionData.maxCVs,
        expiresAt: sessionData.expiresAt,
      }
      setSessionId(sid)
      setJobId(jid)

      setAnonSession(session)
      setExistingSession(null)

      const results = new Array(files.length)
      let completed = 0
      let nextIndex = 0
      const workerCount = Math.min(MAX_PARALLEL_UPLOADS, files.length)

      async function worker() {
        while (nextIndex < files.length) {
          const fileIndex = nextIndex
          nextIndex += 1
          const file = files[fileIndex]
          setCurrentFile(file.name)

          try {
            results[fileIndex] = await processFile(session, file)
          } catch (err) {
            results[fileIndex] = {
              name: file.name,
              status: 'error',
              error: err.message,
            }
          }

          completed += 1
          setProgress(Math.round((completed / files.length) * 100))
          setUploadResults(results.filter(Boolean))
        }
      }

      await Promise.all(Array.from({ length: workerCount }, () => worker()))

      setProgress(100)
      setCurrentFile(null)
      setStep('done')
    } catch (err) {
      toast.error(err.message || t('anonUpload.toasts.genericError'))
      setStep('setup')
    } finally {
      setUploading(false)
    }
  }

  function goToResults() {
    navigate('/results')
  }

  function startNewUpload() {
    clearAnonSession()
    setExistingSession(null)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-8 h-8 rounded-xl bg-brand-600 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">WorkflowPro</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitch />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
            >
              ← {t('publicNav.back')}
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <AnimatePresence mode="wait">
          {/* Setup step */}
          {step === 'setup' && (
            <motion.div
              key="setup"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {t('anonUpload.heading')}
                </h1>
                <p className="text-gray-500">
                  {t('anonUpload.subheading')}
                </p>
              </div>

              {existingSession && (
                <div className="card p-5 mb-5 border-brand-100 bg-white">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {t('anonUpload.existingTitle')}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {t('anonUpload.existingDescription')}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="secondary" size="sm" onClick={startNewUpload}>
                        {t('anonUpload.startOver')}
                      </Button>
                      <Button variant="primary" size="sm" onClick={() => navigate('/results')}>
                        {t('anonUpload.continue')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Job details */}
              <div className="card p-6 mb-5">
                <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-brand-600" />
                  {t('anonUpload.jobDetails')}
                  <span className="text-xs text-gray-400 font-normal ml-1">
                    {t('anonUpload.optionalContext')}
                  </span>
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="label">{t('anonUpload.jobTitle')}</label>
                    <input
                      type="text"
                      className="input"
                      placeholder={t('anonUpload.jobTitlePlaceholder')}
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="label">{t('anonUpload.jobDescription')}</label>
                    <textarea
                      className="input resize-none"
                      rows={5}
                      placeholder={t('anonUpload.jobDescriptionPlaceholder')}
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Dropzone */}
              <div className="card p-6 mb-5">
                <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-brand-600" />
                  {t('anonUpload.uploadCvs')}
                  <span className="text-xs text-gray-400 font-normal ml-1">
                    {t('anonUpload.added', { count: files.length, max: MAX_FILES })}
                  </span>
                </h2>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                    isDragActive
                      ? 'border-brand-400 bg-brand-50'
                      : files.length >= MAX_FILES
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                      : 'border-gray-300 hover:border-brand-400 hover:bg-brand-50'
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload
                    className={`w-10 h-10 mx-auto mb-3 ${
                      isDragActive ? 'text-brand-600' : 'text-gray-400'
                    }`}
                  />
                  {files.length >= MAX_FILES ? (
                    <p className="text-gray-500 font-medium">
                      {t('anonUpload.maxReached', { count: MAX_FILES })}
                    </p>
                  ) : isDragActive ? (
                    <p className="text-brand-600 font-medium">
                      {t('anonUpload.dropHere')}
                    </p>
                  ) : (
                    <>
                      <p className="text-gray-700 font-medium mb-1">
                        {t('anonUpload.dragHere')}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {t('anonUpload.browse', { count: MAX_FILES })}
                      </p>
                    </>
                  )}
                </div>

                {/* File list */}
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2.5"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-4 h-4 text-brand-600 shrink-0" />
                          <span className="text-sm text-gray-700 truncate">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-400 shrink-0">
                            {formatFileSize(file.size)}
                          </span>
                        </div>
                        <button
                          onClick={() => removeFile(i)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                variant="primary"
                fullWidth
                size="lg"
                loading={uploading}
                disabled={files.length === 0}
                onClick={handleUpload}
                icon={<Zap className="w-5 h-5" />}
              >
                {files.length > 0
                  ? t('anonUpload.rankCvs', { count: files.length, plural: files.length === 1 ? '' : 's' })
                  : t('anonUpload.rankNoCount')}
              </Button>

              <p className="text-center text-xs text-gray-400 mt-3">
                {t('anonUpload.freeNote')}
              </p>
            </motion.div>
          )}

          {/* Uploading step */}
          {step === 'uploading' && (
            <motion.div
              key="uploading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="card p-8">
                <div className="w-16 h-16 rounded-2xl bg-brand-100 flex items-center justify-center mx-auto mb-6">
                  <Zap className="w-8 h-8 text-brand-600 animate-pulse" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('anonUpload.processingTitle')}
                </h2>
                <p className="text-gray-500 mb-6">
                  {currentFile
                    ? t('anonUpload.processingFile', { file: currentFile })
                    : t('anonUpload.almostDone')}
                </p>

                {/* Progress bar */}
                <div className="w-full bg-gray-100 rounded-full h-2 mb-6">
                  <motion.div
                    className="h-2 rounded-full bg-brand-600"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>

                {/* Processing steps */}
                <div className="space-y-2 text-left">
                  {[
                    t('anonUpload.processingSteps.0'),
                    t('anonUpload.processingSteps.1'),
                    t('anonUpload.processingSteps.2'),
                    t('anonUpload.processingSteps.3'),
                  ].map((step, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 text-sm ${
                        progress > i * 25
                          ? 'text-gray-700'
                          : 'text-gray-300'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center ${
                          progress > i * 25
                            ? 'bg-brand-100'
                            : 'bg-gray-100'
                        }`}
                      >
                        {progress > i * 25 && (
                          <div className="w-2 h-2 rounded-full bg-brand-600" />
                        )}
                      </div>
                      {step}
                    </div>
                  ))}
                </div>

                {/* Partial results */}
                {uploadResults.length > 0 && (
                  <div className="mt-6 space-y-2 text-left">
                    {uploadResults.map((r, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5"
                      >
                        {r.status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                        )}
                        <span className="text-sm text-gray-700 flex-1 truncate">
                          {r.candidateName || r.name}
                        </span>
                        {r.matchScore !== undefined && (
                          <span className="text-sm font-bold text-brand-600">
                            {r.matchScore}%
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Done step */}
          {step === 'done' && (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="card p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('anonUpload.doneTitle')}
                </h2>
                <p className="text-gray-500 mb-6">
                  {t('anonUpload.processedSuccessfully', {
                    count: uploadResults.filter((r) => r.status === 'success').length,
                  })}
                </p>

                <div className="space-y-2 mb-6 text-left">
                  {uploadResults.map((r, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3"
                    >
                      {r.status === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                      )}
                      <span className="text-sm text-gray-700 flex-1">
                        {r.candidateName || r.name}
                      </span>
                      {r.matchScore !== undefined && (
                        <span className="text-sm font-bold text-brand-600">
                          {t('anonUpload.score', { score: r.matchScore })}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                <Button
                  variant="primary"
                  fullWidth
                  size="lg"
                  onClick={goToResults}
                  icon={<ArrowRight className="w-5 h-5" />}
                  iconPosition="right"
                >
                  {t('anonUpload.seeRankings')}
                </Button>

                <p className="text-xs text-gray-400 mt-3">
                  {t('anonUpload.saveNote')}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
